import { useState } from 'react'
import { useCalibrationProfiles, useDefaultCalibrationProfile } from '../../hooks/useCalibrationProfiles'
import { useUpdateRun } from '../../hooks/useRuns'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'

interface CalibrationProfileSelectorProps {
  runId: string
  currentProfileId?: string | null
}

export function CalibrationProfileSelector({ runId, currentProfileId }: CalibrationProfileSelectorProps) {
  const { data: profiles, isLoading } = useCalibrationProfiles()
  const defaultProfile = useDefaultCalibrationProfile()
  const updateRunMutation = useUpdateRun()
  const [selectedProfileId, setSelectedProfileId] = useState<string>(currentProfileId || 'default')

  const allProfiles = [defaultProfile, ...(profiles || [])]

  const handleApplyProfile = async () => {
    const profileId = selectedProfileId === 'default' ? null : selectedProfileId
    await updateRunMutation.mutateAsync({
      id: runId,
      updates: { calibration_profile_id: profileId }
    })
  }

  if (isLoading) {
    return <div className="p-8 text-center text-neutral-600">Loading profiles...</div>
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-primary-900">Select Calibration Profile</CardTitle>
          <p className="text-sm text-neutral-600">
            Choose a pace model for accurate time estimates
          </p>
        </CardHeader>
        <CardContent>
          <RadioGroup value={selectedProfileId} onValueChange={setSelectedProfileId}>
            {allProfiles.map((profile) => (
              <div key={profile.id} className="flex items-start space-x-4 p-6 border border-neutral-200 rounded-2xl hover:bg-neutral-50 hover:border-neutral-300 transition-all">
                <RadioGroupItem value={profile.id} id={profile.id} className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor={profile.id} className="font-medium cursor-pointer flex items-center gap-3">
                    <span className="text-primary-900">{profile.name}</span>
                    {profile.id === 'default' && (
                      <Badge className="bg-primary-100 text-primary-700">Default</Badge>
                    )}
                    {profile.id === currentProfileId && (
                      <Badge className="bg-emerald-100 text-emerald-700">Currently Applied</Badge>
                    )}
                  </Label>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-600 font-medium">Base pace:</span>
                      <span className="font-mono text-primary-900 font-semibold">{profile.beta0_base_pace_min_per_km.toFixed(2)} min/km</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-600 font-medium">Uphill:</span>
                      <span className="font-mono text-primary-900 font-semibold">+{(profile.beta1_uphill_min_per_m_per_km * 100).toFixed(1)}s per 100m/km</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                      <span className="text-neutral-600 font-medium">Downhill:</span>
                      <span className="font-mono text-primary-900 font-semibold">{(profile.beta2_downhill_min_per_m_per_km * 100).toFixed(1)}s per 100m/km</span>
                    </div>
                    {profile.use_fatigue && (
                      <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                        <span className="text-neutral-600 font-medium">Fatigue:</span>
                        <span className="font-mono text-primary-900 font-semibold">+{(profile.beta3_fatigue_min_per_km * 1000).toFixed(1)}s per km</span>
                      </div>
                    )}
                  </div>

                  {profile.id !== 'default' && profile.created_at && (
                    <div className="mt-3 text-xs text-neutral-500">
                      Created {new Date(profile.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </RadioGroup>

          <Button
            onClick={handleApplyProfile}
            className="mt-8 w-full"
            size="lg"
            disabled={updateRunMutation.isPending || selectedProfileId === currentProfileId}
          >
            {updateRunMutation.isPending ? 'Applying...' : 'Apply Profile'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-primary-50 border-primary-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold text-primary-900">How Pace Models Work</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-primary-800 space-y-3">
          <p>
            <strong className="text-primary-900">Base pace</strong> is your flat ground running pace on this type of terrain.
          </p>
          <p>
            <strong className="text-primary-900">Uphill factor</strong> adds time for elevation gain (positive values slow you down).
          </p>
          <p>
            <strong className="text-primary-900">Downhill factor</strong> adjusts time for elevation loss (negative values speed you up, but capped).
          </p>
          {allProfiles.some(p => p.use_fatigue) && (
            <p>
              <strong className="text-primary-900">Fatigue factor</strong> accounts for cumulative distance slowing you down over long runs.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}